// News-event feed (D-072/D-073) — the LLM-classified event stream that a
// `newsEvent` strategy operand reacts to. Structurally the twin of the
// econ-calendar (D-065, econ-calendar.ts): the SAME list is fed to the in-app
// backtester's evaluate() AND exported to the box as news_feed.json, so a
// `newsEvent` rule resolves identically live and in backtest (backtest == live,
// D-064). The AI classifies each event ONCE server-side into
// {scope, impact, sentiment} — it never forecasts price; the user's mechanical,
// tested rule owns the reaction (Kernel Never-Do #2 — we sell the keyboard).
//
// Sentiment is about the NEWS (favourable / unfavourable to the entity), never a
// price call. Events are FACTS with a timestamp; the feed is deterministic, so
// there is no live LLM call in the trade loop.
//
// STATUS — surface only. The backend news engine that produces this feed is not
// built yet, so `newsFeedEvents` returns nothing today: a `newsEvent` block
// AUTHORS and BACKTESTS now (as always-quiet, which is honest — we have no
// historical event record), and begins to fire the day the backend feed lands.
// Nothing else about the builder changes when it does.

export type NewsScope = "market" | "instrument";
export type NewsImpact = "high" | "medium" | "low";
export type NewsSentiment = "positive" | "negative" | "neutral";

/** One classified event. `symbol` is set only for instrument-scope events. */
export interface NewsFeedEvent {
  at: number;                 // epoch ms the event occurred / was classified
  scope: NewsScope;           // Broad-Market vs Specific-Instrument
  symbol?: string;            // set when scope === "instrument"
  impact: NewsImpact;         // classified magnitude
  sentiment: NewsSentiment;   // about the news, NOT a price forecast
  name?: string;              // short label, for journal annotation only
}

/**
 * Classified events overlapping [fromMs, toMs] relevant to `symbol`:
 * market-scope events are always included; instrument-scope events are filtered
 * to `symbol` (case-insensitive, root ticker). Empty until the backend news
 * engine lands — see the module header.
 */
export function newsFeedEvents(_fromMs: number, _toMs: number, _symbol?: string): NewsFeedEvent[] {
  return [];
}

/**
 * The news-feed file the agent writes to the box (`~/.tickahead/news_feed.json`),
 * the `newsEvent` twin of `econCalendarFile` (D-065). The box's agent handles a
 * `set-news` command exactly as it handles `set-calendar`. Empty until the
 * backend lands.
 */
export function newsFeedFile(now: number = Date.now()): { refreshedAt: number; events: NewsFeedEvent[] } {
  return { refreshedAt: now, events: [] };
}
